/**
 * Service tiroir-caisse (cash drawer)
 * ------------------------------------
 * Ouvre le tiroir-caisse branché en RJ11 sur l'imprimante ticket thermique
 * en envoyant la commande ESC/POS "drawer kick" (ESC p) en RAW via QZ Tray.
 *
 * Prérequis poste de caisse : QZ Tray installé et démarré (https://qz.io).
 * V1 : connexion non signée (QZ affiche une boîte "Allow" - cocher "Remember").
 */
import qz from "qz-tray";

const STORAGE_KEY = "cashdrawer_config_v1";

// ESC p <pin> <t1> <t2> - impulsion d'ouverture du tiroir
const KICK = {
    standard: "\x1B\x70\x00\x19\xFA", // pin 2 (la majorité des tiroirs)
    pin5: "\x1B\x70\x01\x19\xFA",     // pin 5
};

const DEFAULT_CONFIG = {
    printerName: "",
    pin: "standard",
    cooldownMs: 5000,
};

let lastOpenAt = 0;

function notify(level, message) {
    if (typeof window !== "undefined" && window.toastr && typeof window.toastr[level] === "function") {
        window.toastr[level](message, "<b></b>");
    }
}

export function getDrawerConfig() {
    try {
        const raw = localStorage.getItem(STORAGE_KEY);
        if (raw) return { ...DEFAULT_CONFIG, ...JSON.parse(raw) };
    } catch (e) { /* config corrompue → défauts */ }
    return { ...DEFAULT_CONFIG };
}

export function saveDrawerConfig(config) {
    const merged = { ...getDrawerConfig(), ...config };
    localStorage.setItem(STORAGE_KEY, JSON.stringify(merged));
    return merged;
}

async function ensureConnection() {
    // V1 : mode non signé - QZ Tray demandera une autorisation "Allow" sur le poste
    qz.security.setCertificatePromise((resolve) => resolve());
    qz.security.setSignaturePromise(() => (resolve) => resolve());

    if (!qz.websocket.isActive()) {
        await qz.websocket.connect({ retries: 1, delay: 1 });
    }
}

/** Liste les imprimantes vues par QZ Tray (pour l'UI de configuration). */
export async function listPrinters() {
    await ensureConnection();
    return qz.printers.find();
}

/**
 * Envoie l'impulsion d'ouverture au tiroir.
 * Rejette avec {code:'NO_PRINTER'} si aucune imprimante n'est configurée.
 */
export async function openCashDrawer() {
    const config = getDrawerConfig();
    if (!config.printerName) {
        const err = new Error("Aucune imprimante ticket configurée");
        err.code = "NO_PRINTER";
        throw err;
    }
    await ensureConnection();
    const qzConfig = qz.configs.create(config.printerName);
    const data = [{ type: "raw", format: "command", flavor: "plain", data: KICK[config.pin] || KICK.standard }];
    await qz.print(qzConfig, data);
    lastOpenAt = Date.now();
}

/**
 * Ouverture "sûre" : ne lève jamais d'exception vers l'appelant,
 * affiche un message d'aide en cas de problème, applique un cooldown
 * anti double-ouverture (validation + option d'impression peuvent se cumuler).
 */
export async function tryOpenDrawer({ silent = false } = {}) {
    const config = getDrawerConfig();
    if (Date.now() - lastOpenAt < (config.cooldownMs || DEFAULT_CONFIG.cooldownMs)) {
        return { success: true, skipped: true };
    }
    try {
        await openCashDrawer();
        if (!silent) notify("success", "Tiroir-caisse ouvert");
        return { success: true };
    } catch (e) {
        if (e && e.code === "NO_PRINTER") {
            notify("warning", "Tiroir-caisse : configurez l'imprimante ticket (Options &gt; Tiroir-caisse).");
        } else {
            notify("info", "Tiroir-caisse : QZ Tray non détecté. Installez et démarrez QZ Tray sur ce poste (qz.io).");
        }
        return { success: false, error: e };
    }
}

/**
 * Ouverture automatique pour rendre la monnaie :
 * appelée à la validation de la vente quand le montant reçu est valide
 * et qu'il reste de la monnaie à rendre au client.
 */
export async function openForChange(changeAmount) {
    if (parseFloat(changeAmount) > 0) {
        return tryOpenDrawer({ silent: true });
    }
    return { success: true, skipped: true };
}
